import dayjs from 'dayjs'
import isoWeek from 'dayjs/plugin/isoWeek'
import { _x } from '@wordpress/i18n'

dayjs.extend(isoWeek)

export const DATE_FORMAT_DEFAULT = _x('Y/m/d', 'Date', 'icf')
export const TIME_FORMAT_DEFAULT = _x('h:i a', 'Time', 'icf')
export const DATE_SEPARATOR_DEFAULT = _x('-', 'Date Range Separator', 'icf')
export const TIME_SEPARATOR_DEFAULT = _x('-', 'Time Range Separator', 'icf')

export const NOW = dayjs()

const HOUR_MS = 3_600_000

/**
 * Aktuelle Zeit auf die volle Stunde gerundet — für Algolia-Filter statt
 * Date.now() verwenden, damit Queries innerhalb einer Stunde für alle Besucher
 * identisch (und damit cachebar) bleiben.
 */
export function nowFloorHour(): number {
  return Math.floor(Date.now() / HOUR_MS) * HOUR_MS
}

export function nowCeilHour(): number {
  return Math.ceil(Date.now() / HOUR_MS) * HOUR_MS
}

export const TODAY_START = dayjs().startOf('day')
export const TODAY_END = dayjs().endOf('day')

export const TOMORROW_START = dayjs().add(1, 'day').startOf('day')
export const TOMORROW_END = dayjs().add(1, 'day').endOf('day')

export const WEEK_START = dayjs().startOf('isoWeek')
export const WEEK_END = dayjs().endOf('isoWeek')

export const NEXT_WEEK_START = dayjs().add(1, 'week').startOf('isoWeek')
export const NEXT_WEEK_END = dayjs().add(1, 'week').endOf('isoWeek')

export const CURRENT_MONTH = dayjs().month()
export const NEXT_MONTH_START = dayjs()
  .month(CURRENT_MONTH + 1)
  .startOf('month')
export const NEXT_MONTH_END = dayjs()
  .month(CURRENT_MONTH + 1)
  .endOf('month')

export const YESTERDAY_START = dayjs().subtract(1, 'day').startOf('day')
export const YESTERDAY_END = dayjs().subtract(1, 'day').endOf('day')

export const LAST_WEEK_START = dayjs().subtract(1, 'week').startOf('isoWeek')
export const LAST_WEEK_END = dayjs().subtract(1, 'week').endOf('isoWeek')

export const LAST_MONTH_START = dayjs().subtract(1, 'month').startOf('month')
export const LAST_MONTH_END = dayjs().subtract(1, 'month').endOf('month')

function createDateFilter(
  date1: dayjs.Dayjs,
  date2: dayjs.Dayjs
): [number, number] {
  return [date1.valueOf(), date2.valueOf()]
}

export type TConstantDateFilter = {
  id: string
  label: string
  value: [number, number]
}

export const DATE_FILTERS = [
  {
    id: 'today',
    label: _x('Today', 'Date Filter', 'icf'),
    value: createDateFilter(TODAY_START, TODAY_END)
  },
  {
    id: 'tomorrow',
    label: _x('Tomorrow', 'Date Filter', 'icf'),
    value: createDateFilter(TOMORROW_START, TOMORROW_END)
  },
  {
    id: 'week',
    label: _x('This Week', 'Date Filter', 'icf'),
    value: createDateFilter(WEEK_START, WEEK_END)
  },
  {
    id: 'nextWeek',
    label: _x('Next Week', 'Date Filter', 'icf'),
    value: createDateFilter(NEXT_WEEK_START, NEXT_WEEK_END)
  },
  {
    id: 'nextMonth',
    label: _x('Next Month', 'Date Filter', 'icf'),
    value: createDateFilter(NEXT_MONTH_START, NEXT_MONTH_END)
  },
  {
    id: 'yesterday',
    label: _x('Yesterday', 'Date Filter', 'icf'),
    value: createDateFilter(YESTERDAY_START, YESTERDAY_END)
  },
  {
    id: 'lastWeek',
    label: _x('Last Week', 'Date Filter', 'icf'),
    value: createDateFilter(LAST_WEEK_START, LAST_WEEK_END)
  },
  {
    id: 'lastMonth',
    label: _x('Last Month', 'Date Filter', 'icf'),
    value: createDateFilter(LAST_MONTH_START, LAST_MONTH_END)
  }
] as const satisfies TConstantDateFilter[]

export const DATE_FILTERS_MAP = DATE_FILTERS.reduce(
  (acc, filter) => {
    acc[filter.id] = filter
    return acc
  },
  {} as Record<(typeof DATE_FILTERS)[number]['id'], TConstantDateFilter>
)

export function getDateFilters(id: string): TConstantDateFilter | undefined
export function getDateFilters(ids: string[]): TConstantDateFilter[]
export function getDateFilters(
  ids: string | string[]
): TConstantDateFilter | TConstantDateFilter[] | undefined {
  if (typeof ids === 'string')
    return DATE_FILTERS.find((filter) => filter.id === ids)

  return DATE_FILTERS.filter((filter) => ids.includes(filter.id))
}

export type TDateFilter = (typeof DATE_FILTERS)[number]['id']
